import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Search, UserPlus, Users } from 'lucide-react';
import { Menu, Button, Avatar, Text, Flex, Box, TextInput, Divider } from '@mantine/core';
import { useUsers } from '../hooks/useTodos';
import { useUser } from '../contexts/UserContext';

interface AssigneeDropdownProps {
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  variant?: 'default' | 'inline'; // Add variant prop
}

export const AssigneeDropdown: React.FC<AssigneeDropdownProps & { hideAnyOption?: boolean }> = ({ 
  value, 
  onChange, 
  placeholder = "Select assignee",
  hideAnyOption = false,
  variant = 'default'
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const { data: users = [], isLoading } = useUsers();
  const { currentUser } = useUser();
  
  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (userEmail: string) => {
    onChange(userEmail);
    setSearchTerm('');
  };

  const handleUnselect = () => {
    onChange('__unassigned__');
    setSearchTerm('');
  };

  // Helper function to get first name only
  const getFirstName = (name: string) => {
    if (!name) return '';
    return name.split(' ')[0];
  };

  const selectedUser = users.find(user => user.email === value);
  const isCurrentUser = currentUser && value === currentUser.email;

  if (isLoading) {
    return (
      <Button 
        variant="default"
        size={variant === 'inline' ? 'compact-sm' : 'sm'}
        disabled
        leftSection={<User size={variant === 'inline' ? 12 : 16} />}
      >
        Loading users...
      </Button>
    );
  }

  const getDisplayValue = () => {
    if (variant === 'inline') {
      if (value === '__unassigned__') return 'Unassigned';
      if (!value || value === undefined) return 'Any';
      if (isCurrentUser) return getFirstName(currentUser.name) || 'Me';
      return getFirstName(selectedUser?.name || '') || getFirstName(value.split('@')[0]) || 'User';
    } else {
      if (value === '__unassigned__') return 'Unassigned';
      if (!value || value === undefined) return 'Any assignee';
      if (isCurrentUser) return `${currentUser.name} (Me)`;
      return selectedUser?.name || placeholder;
    }
  };

  return (
    <Menu position="bottom-start" withinPortal>
      <Menu.Target>
        <Button
          variant="default"
          size={variant === 'inline' ? 'compact-sm' : 'sm'}
          leftSection={variant === 'inline' ? <User size={12} /> : <Users size={12} />}
        >
          {getDisplayValue()}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        {/* Search input for large user lists */}
        {users.length >= 10 && (
          <Box p="xs">
            <TextInput
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              leftSection={<Search size={14} />}
              size="sm"
            />
          </Box>
        )}

        {/* Any assignee option */}
        {!hideAnyOption && !searchTerm && (
          <Menu.Item
            onClick={() => {
              onChange('');
              setSearchTerm('');
            }}
            style={{
              backgroundColor: (!value || value === undefined) ? 'rgba(59, 130, 246, 0.1)' : undefined
            }}
          >
            <Flex align="center" gap="sm">
              <Avatar size="sm" color="gray">
                <User size={14} />
              </Avatar>
              <Box>
                <Text size="sm" fw={500} c="var(--app-text-primary)">Any assignee</Text>
                <Text size="xs" c="dimmed">Show tasks for all assignees</Text>
              </Box>
            </Flex>
          </Menu.Item>
        )}

        {/* Unassigned option */}
        {!searchTerm && (
          <Menu.Item
            onClick={handleUnselect}
            style={{
              backgroundColor: value === '__unassigned__' ? 'rgba(59, 130, 246, 0.1)' : undefined
            }}
          >
            <Flex align="center" gap="sm">
              <Avatar size="sm" color="gray">
                <User size={14} />
              </Avatar>
              <Box>
                <Text size="sm" fw={500} c="var(--app-text-primary)">Unassigned</Text>
                <Text size="xs" c="dimmed">No assignee selected</Text>
              </Box>
            </Flex>
          </Menu.Item>
        )}

        {/* Current user (Me) option */}
        {currentUser && (
          (!searchTerm || 
           currentUser.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           currentUser.email.toLowerCase().includes(searchTerm.toLowerCase())
          ) && (
            <Menu.Item
              onClick={() => handleSelect(currentUser.email)}
              style={{
                backgroundColor: value === currentUser.email ? 'rgba(59, 130, 246, 0.1)' : undefined
              }}
            >
              <Flex align="center" gap="sm">
                <Avatar size="sm" color="blue" src={currentUser.avatar}>
                  {!currentUser.avatar && currentUser.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </Avatar>
                <Box>
                  <Text size="sm" fw={500} c="var(--app-text-primary)">{currentUser.name} (Me)</Text>
                  <Text size="xs" c="dimmed">{currentUser.email}</Text>
                </Box>
              </Flex>
            </Menu.Item>
          )
        )}

        {/* Other users */}
        {filteredUsers.filter(user => !currentUser || user.email !== currentUser.email).map((user) => (
          <Menu.Item
            key={user.email}
            onClick={() => handleSelect(user.email)}
            style={{
              backgroundColor: value === user.email ? 'rgba(59, 130, 246, 0.1)' : undefined
            }}
          >
            <Flex align="center" gap="sm">
              <Avatar size="sm" src={user.avatar}>
                {!user.avatar && user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </Avatar>
              <Box>
                <Text size="sm" fw={500} c="var(--app-text-primary)">{user.name}</Text>
                <Text size="xs" c="dimmed">{user.email}</Text>
              </Box>
            </Flex>
          </Menu.Item>
        ))}

        {/* Divider before invite option */}
        {!searchTerm && <Divider />}

        {/* Invite user option */}
        {!searchTerm && (
          <Menu.Item
            onClick={() => {
              navigate('/settings', { state: { section: 'contacts' } });
            }}
            color="blue"
          >
            <Flex align="center" gap="sm">
              <Avatar size="sm" color="blue">
                <UserPlus size={14} />
              </Avatar>
              <Box>
                <Text size="sm" fw={500} c="var(--app-text-primary)">Invite user</Text>
                <Text size="xs" c="dimmed">Add a new user to your team</Text>
              </Box>
            </Flex>
          </Menu.Item>
        )}

        {/* No results */}
        {filteredUsers.length === 0 && searchTerm && (
          <Box p="md" ta="center">
            <Text size="sm" c="var(--app-text-secondary)">No users found</Text>
          </Box>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
